import { Biquad } from './biquad';

/**
 * Integrated loudness (LUFS) per ITU-R BS.1770-4 / EBU R128: K-weighting, 400 ms
 * blocks with 75 % overlap, absolute (-70 LUFS) and relative (-10 LU) gating.
 * Streaming, so arbitrarily long audio can be measured in constant memory.
 */

/** Loudness target used by YouTube, Instagram, TikTok and Spotify. */
export const SOCIAL_TARGET_LUFS = -14;
/** Keep peaks below -1 dBFS after gain so lossy encoding does not clip. */
export const PEAK_CEILING = 10 ** (-1 / 20);

/** K-weighting coefficients for any sample rate (libebur128 formulation). */
function createKWeighting(sampleRate: number): [Biquad, Biquad] {
  let K = Math.tan((Math.PI * 1681.974450955533) / sampleRate);
  const Q1 = 0.7071752369554196;
  const Vh = 10 ** (3.999843853973347 / 20);
  const Vb = Vh ** 0.4996667741545416;
  let a0 = 1 + K / Q1 + K * K;
  const shelf = new Biquad(
    (Vh + (Vb * K) / Q1 + K * K) / a0,
    (2 * (K * K - Vh)) / a0,
    (Vh - (Vb * K) / Q1 + K * K) / a0,
    (2 * (K * K - 1)) / a0,
    (1 - K / Q1 + K * K) / a0,
  );
  K = Math.tan((Math.PI * 38.13547087602444) / sampleRate);
  const Q2 = 0.5003270373238773;
  a0 = 1 + K / Q2 + K * K;
  const highPass = new Biquad(1, -2, 1, (2 * (K * K - 1)) / a0, (1 - K / Q2 + K * K) / a0);
  return [shelf, highPass];
}

const blockLoudness = (meanSquare: number): number => -0.691 + 10 * Math.log10(meanSquare);

export interface LoudnessResult {
  /** Integrated loudness in LUFS, or null for silent / too short audio. */
  readonly integratedLufs: number | null;
  /** Sample peak (linear, 0..1+). */
  readonly peak: number;
}

export class LoudnessMeter {
  private filters: Array<[Biquad, Biquad]> = [];
  private readonly stepSize: number;
  private readonly stepsPerBlock = 4;
  /** Sum of squares of the current 100 ms step, summed over channels. */
  private stepEnergy = 0;
  private stepFill = 0;
  private readonly recentSteps: number[] = [];
  private readonly blocks: number[] = [];
  private peak = 0;

  constructor(private readonly sampleRate: number) {
    if (!(sampleRate > 0)) throw new RangeError('sampleRate must be positive');
    this.stepSize = Math.round(sampleRate * 0.1);
  }

  /** Adds planar audio; all channels must have the same length. */
  push(channels: readonly Float32Array[]): void {
    if (channels.length === 0) return;
    while (this.filters.length < channels.length) this.filters.push(createKWeighting(this.sampleRate));
    const frames = channels[0]!.length;
    for (let i = 0; i < frames; i++) {
      let energy = 0;
      for (let c = 0; c < channels.length; c++) {
        const sample = channels[c]![i] ?? 0;
        const abs = Math.abs(sample);
        if (abs > this.peak) this.peak = abs;
        const [shelf, highPass] = this.filters[c]!;
        const weighted = highPass.process(shelf.process(sample));
        energy += weighted * weighted;
      }
      this.stepEnergy += energy;
      if (++this.stepFill === this.stepSize) this.completeStep();
    }
  }

  result(): LoudnessResult {
    const absoluteGated = this.blocks.filter((ms) => blockLoudness(ms) > -70);
    if (absoluteGated.length === 0) return { integratedLufs: null, peak: this.peak };
    const relativeThreshold = blockLoudness(mean(absoluteGated)) - 10;
    const gated = absoluteGated.filter((ms) => blockLoudness(ms) > relativeThreshold);
    return { integratedLufs: blockLoudness(mean(gated.length > 0 ? gated : absoluteGated)), peak: this.peak };
  }

  private completeStep(): void {
    this.recentSteps.push(this.stepEnergy / this.stepSize);
    this.stepEnergy = 0;
    this.stepFill = 0;
    if (this.recentSteps.length > this.stepsPerBlock) this.recentSteps.shift();
    if (this.recentSteps.length === this.stepsPerBlock) this.blocks.push(mean(this.recentSteps));
  }
}

function mean(values: readonly number[]): number {
  let sum = 0;
  for (const value of values) sum += value;
  return sum / values.length;
}

/**
 * Linear gain that brings `measured` to `targetLufs` without pushing peaks over the
 * ceiling. Returns 1 when nothing can be measured.
 */
export function normalizationGain(measured: LoudnessResult, targetLufs = SOCIAL_TARGET_LUFS): number {
  if (measured.integratedLufs === null || measured.peak <= 0) return 1;
  const loudnessGain = 10 ** ((targetLufs - measured.integratedLufs) / 20);
  const peakGain = PEAK_CEILING / measured.peak;
  return Math.min(loudnessGain, peakGain, 10 ** (24 / 20));
}
